import { useState } from 'react'
import { ShieldCheck } from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  SaAlert,
  SaButton,
  SaCard,
} from '../design-system/components'
import {
  SA_FIELD_INPUT_CLASS,
  SA_FIELD_LABEL_CLASS,
} from '../mollie-oauth/saFormFieldClasses'
import {
  createTotpLoginChallenge,
  verifyTotpLoginChallenge,
} from './mfa.service'

export interface MfaChallengeScreenProps {
  factorId: string
  onVerified: () => void
  purpose?: 'superadmin' | 'impersonation'
}

const PURPOSE_COPY = {
  superadmin: {
    title: 'Tweefactorauthenticatie',
    description:
      'Voer de 6-cijferige code uit je authenticator-app in om toegang te krijgen tot het superadmin-dashboard.',
  },
  impersonation: {
    title: 'Bevestig 2FA om door te gaan',
    description:
      'Voer je 6-cijferige code in om het klantportaal of de backoffice te bekijken als deze gebruiker.',
  },
} as const

export function MfaChallengeScreen ({
  factorId,
  onVerified,
  purpose = 'superadmin',
}: MfaChallengeScreenProps) {
  const [code, setCode] = useState('')
  const [acting, setActing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit (event: React.FormEvent) {
    event.preventDefault()
    setActing(true)
    setError(null)
    try {
      const challengeId = await createTotpLoginChallenge(factorId)
      await verifyTotpLoginChallenge(factorId, challengeId, code)
      onVerified()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Verificatie mislukt')
    } finally {
      setActing(false)
    }
  }

  const copy = PURPOSE_COPY[purpose]

  return (
    <div className="flex min-h-dvh items-center justify-center bg-sa-background px-4 py-8">
      <SaCard padding="md" className="w-full max-w-md">
        <div className="mb-4 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-sa-primary" strokeWidth={1.75} aria-hidden />
          <h1 className="text-lg font-semibold text-sa-foreground">{copy.title}</h1>
        </div>
        <p className="mb-6 text-sm text-sa-muted-foreground">
          {copy.description}
        </p>

        {error ? (
          <SaAlert className="mb-4" variant="danger" title="Verificatie mislukt" description={error} />
        ) : null}

        <form className="space-y-4" onSubmit={(event) => void handleSubmit(event)}>
          <div>
            <label htmlFor="mfa-login-code" className={SA_FIELD_LABEL_CLASS}>
              Verificatiecode
            </label>
            <input
              id="mfa-login-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              className={SA_FIELD_INPUT_CLASS}
              placeholder="000000"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
              disabled={acting}
            />
          </div>
          <SaButton
            type="submit"
            variant="primary"
            size="sm"
            loading={acting}
            disabled={code.length !== 6}
            className="w-full"
          >
            Verifiëren
          </SaButton>
        </form>

        <p className="mt-4 text-center text-xs text-sa-muted-foreground">
          Problemen met 2FA?{' '}
          <Link to="/superadmin/security" className="font-medium text-sa-primary hover:underline">
            Ga naar Beveiliging
          </Link>
        </p>
      </SaCard>
    </div>
  )
}
